/**
 * SOLOMON — SPEECH RECOGNITION HOOK
 * Phase 1 / Day 6: Voice Input
 *
 * Uses the browser's Web Speech API for speech-to-text.
 * Works on Chrome, Edge, Safari (webkit prefix).
 * Firefox has limited/no support — degrades gracefully.
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// Check browser support
const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition || null;

export function useSpeechRecognition({ onResult, onError, autoSend = true, silenceDelay = 1500 }) {
  const [isListening,    setIsListening]    = useState(false);
  const [transcript,     setTranscript]     = useState('');
  const [interimText,    setInterimText]    = useState('');
  const [isSupported,    setIsSupported]    = useState(!!SpeechRecognition);
  const [lastError,      setLastError]      = useState(null);

  const recognitionRef  = useRef(null);
  const silenceTimerRef = useRef(null);
  const activeRef       = useRef(false);

  // Cleanup on unmount AND when the tab/window is closed
  useEffect(() => {
    const handleUnload = () => {
      // Force-stop mic immediately when page closes — prevents the
      // macOS mic indicator from staying on after the tab is closed
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      // React unmount cleanup (tab navigation within the app)
      recognitionRef.current?.stop();
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  const stopListening = useCallback(() => {
    activeRef.current = false;
    recognitionRef.current?.stop();
    setIsListening(false);
    setInterimText('');
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
  }, []);

  const startListening = useCallback(() => {
    if (!SpeechRecognition) {
      setLastError('Speech recognition is not supported in this browser. Try Chrome or Edge.');
      onError?.('not_supported');
      return;
    }

    // Stop any existing session first
    recognitionRef.current?.stop();

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    recognition.continuous      = false;    // Single utterance mode
    recognition.interimResults  = true;     // Show partial results while speaking
    recognition.lang            = 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setTranscript('');
      setInterimText('');
      setLastError(null);
      activeRef.current = true;
    };

    recognition.onresult = (event) => {
      let finalText   = '';
      let interimText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText   += result[0].transcript;
        } else {
          interimText += result[0].transcript;
        }
      }

      if (interimText) setInterimText(interimText);

      if (finalText) {
        setTranscript(finalText.trim());
        setInterimText('');

        // Notify parent with the transcript
        onResult?.(finalText.trim());

        // Auto-send after silence delay
        if (autoSend) {
          if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = setTimeout(() => {
            if (activeRef.current) stopListening();
          }, silenceDelay);
        }
      }
    };

    recognition.onerror = (event) => {
      const errorMap = {
        'not-allowed':    'Microphone access was denied. Please allow mic access in your browser.',
        'no-speech':      'No speech detected. Try speaking louder or closer to the mic.',
        'audio-capture':  'No microphone found. Please connect a microphone.',
        'network':        'Network error during speech recognition.',
        'aborted':        null, // User stopped — not an error
      };

      const msg = errorMap[event.error] || `Speech error: ${event.error}`;
      if (msg) {
        setLastError(msg);
        onError?.(event.error, msg);
      }
      setIsListening(false);
      activeRef.current = false;
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimText('');
      activeRef.current = false;
    };

    try {
      recognition.start();
    } catch (err) {
      setLastError('Could not start microphone. Please check permissions.');
      setIsListening(false);
    }
  }, [onResult, onError, autoSend, silenceDelay, stopListening]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isListening,
    isSupported,
    transcript,
    interimText,         // Partial text while speaking
    lastError,
    startListening,
    stopListening,
    toggleListening,
    clearError: () => setLastError(null),
  };
}
